import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';

const INDUSTRY_OPTIONS = [
  'Construction (General)', 'Residential Construction', 'Commercial Construction',
  'Roofing', 'Plumbing', 'Electrical', 'HVAC', 'Painting & Finishing',
  'Landscaping & Groundskeeping', 'Logistics & Transportation', 'Warehousing & Distribution',
  'Manufacturing', 'Restaurant & Food Service', 'Hospitality & Hotels',
  'Cleaning & Janitorial', 'Agriculture & Farming', 'Automotive & Repair',
  'Oil & Gas', 'Solar & Renewable Energy', 'Property Management',
  'Retail', 'Healthcare Support', 'Other',
];

const BUSINESS_TYPE_OPTIONS = [
  'General Contractor', 'Subcontractor', 'Staffing Agency', 'Sole Proprietor',
  'LLC', 'Corporation', 'Non-profit', 'Other',
];

function CompanyProfilePage() {
  const user = getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [role, setRole] = useState('');

  const [loaded, setLoaded] = useState(null);
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessTypeOther, setBusinessTypeOther] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLogoURL, setCompanyLogoURL] = useState(null);

  const logoInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const rawInd = data.profile?.industry ?? '';
        const isCustomInd = rawInd !== '' && !INDUSTRY_OPTIONS.slice(0, -1).includes(rawInd);
        const rawBT = data.profile?.businessType ?? '';
        const isCustomBT = rawBT !== '' && !BUSINESS_TYPE_OPTIONS.slice(0, -1).includes(rawBT);
        const vals = {
          company: data.profile?.company ?? '',
          location: data.profile?.location ?? '',
          bio: data.profile?.bio ?? '',
          phoneNumber: data.phoneNumber ?? '',
          companyLogoURL: data.companyLogoURL ?? null,
          industry: isCustomInd ? 'Other' : rawInd,
          industryOther: isCustomInd ? rawInd : '',
          businessType: isCustomBT ? 'Other' : rawBT,
          businessTypeOther: isCustomBT ? rawBT : '',
          companyEmail: data.profile?.companyEmail ?? '',
          companyWebsite: data.profile?.companyWebsite ?? '',
        };
        setCompany(vals.company);
        setLocation(vals.location);
        setBio(vals.bio);
        setPhoneNumber(vals.phoneNumber);
        setIndustry(vals.industry);
        setIndustryOther(vals.industryOther);
        setBusinessType(vals.businessType);
        setBusinessTypeOther(vals.businessTypeOther);
        setCompanyEmail(vals.companyEmail);
        setCompanyWebsite(vals.companyWebsite);
        setCompanyLogoURL(vals.companyLogoURL);
        setRole(data.role || data.profile?.role || '');
        setLoaded(vals);
      }
      setLoading(false);
    });
  }, [user]);

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoError('');
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setLogoError('Only JPG and PNG files are supported.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Image must be 2MB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
      setLogoFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !user) return;
    setLogoUploading(true);
    setLogoError('');
    try {
      const storage = getStorage();
      const fileRef = storageRef(storage, `users/${user.uid}/company-logo.jpg`);
      await uploadBytes(fileRef, logoFile);
      const downloadURL = await getDownloadURL(fileRef);
      await updateDoc(doc(db, 'users', user.uid), { companyLogoURL: downloadURL });
      setCompanyLogoURL(downloadURL);
      setLogoFile(null);
      setLogoPreview('');
      if (logoInputRef.current) logoInputRef.current.value = '';
    } catch {
      setLogoError('Upload failed. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleWebsiteBlur = () => {
    const v = companyWebsite.trim();
    if (v && !v.startsWith('http')) setCompanyWebsite('https://' + v);
  };

  const handleSave = async () => {
    if (!company.trim()) {
      setSaveStatus('error');
      setSaveMessage('Company name is required.');
      return;
    }
    if (companyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail.trim())) {
      setSaveStatus('error');
      setSaveMessage('Please enter a valid company email address.');
      return;
    }
    setSaving(true);
    setSaveStatus('');
    setSaveMessage('');
    try {
      const indValue = industry === 'Other' ? industryOther.trim() : industry;
      const btValue = businessType === 'Other' ? businessTypeOther.trim() : businessType;
      const updates = {};
      if (company.trim() !== (loaded?.company ?? '')) updates['profile.company'] = company.trim();
      if (location.trim() !== (loaded?.location ?? '')) updates['profile.location'] = location.trim();
      if (bio.trim() !== (loaded?.bio ?? '')) updates['profile.bio'] = bio.trim();
      if (phoneNumber.trim() !== (loaded?.phoneNumber ?? '')) updates.phoneNumber = phoneNumber.trim();
      if (indValue !== (loaded?.industry ?? '')) updates['profile.industry'] = indValue;
      if (btValue !== (loaded?.businessType ?? '')) updates['profile.businessType'] = btValue;
      if (companyEmail.trim() !== (loaded?.companyEmail ?? '')) updates['profile.companyEmail'] = companyEmail.trim();
      if (companyWebsite.trim() !== (loaded?.companyWebsite ?? '')) updates['profile.companyWebsite'] = companyWebsite.trim();

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'users', user.uid), updates);
        setLoaded(prev => ({
          ...prev,
          company: company.trim(),
          location: location.trim(),
          bio: bio.trim(),
          phoneNumber: phoneNumber.trim(),
          industry: indValue,
          industryOther: industry === 'Other' ? industryOther.trim() : '',
          businessType: btValue,
          businessTypeOther: businessType === 'Other' ? businessTypeOther.trim() : '',
          companyEmail: companyEmail.trim(),
          companyWebsite: companyWebsite.trim(),
        }));
      }
      setSaveStatus('success');
      setSaveMessage('Profile saved.');
      setTimeout(() => { setSaveStatus(''); setSaveMessage(''); }, 2000);
    } catch {
      setSaveStatus('error');
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box',
    color: '#1a202c',
    backgroundColor: 'white',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '500',
    fontSize: '14px',
    color: '#374151',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  if (role !== 'employer') {
    return (
      <div style={{ padding: '2rem' }}>
        <p style={{ color: '#6b7280' }}>This page is for employers.</p>
      </div>
    );
  }

  const logoInitial = company ? company[0].toUpperCase() : '?';

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ backgroundColor: '#F8FAFC', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '2rem' }}>

        {/* Company logo */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Company Logo</label>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{
              width: '80px',
              height: '80px',
              flexShrink: 0,
              borderRadius: '8px',
              border: (logoPreview || companyLogoURL) ? '1px solid #e5e7eb' : '2px dashed #d1d5db',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: (logoPreview || companyLogoURL) ? 'transparent' : '#f9fafb',
            }}>
              {(logoPreview || companyLogoURL) ? (
                <img
                  src={logoPreview || companyLogoURL}
                  alt="Company logo"
                  style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain' }}
                />
              ) : (
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#E8590C' }}>{logoInitial}</span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleLogoSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => logoInputRef.current?.click()}
                style={{ backgroundColor: 'white', color: '#374151', padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}
              >
                Choose Image
              </button>
              <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
                Any shape · JPG or PNG · Max 2MB
              </div>
              {logoError && <div style={{ color: '#991b1b', fontSize: '13px', marginTop: '0.35rem' }}>{logoError}</div>}
              {logoFile && (
                <button
                  onClick={handleLogoUpload}
                  disabled={logoUploading}
                  style={{ marginTop: '0.5rem', backgroundColor: '#E8590C', color: 'white', padding: '0.45rem 0.85rem', borderRadius: '6px', border: 'none', cursor: logoUploading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', opacity: logoUploading ? 0.6 : 1 }}
                >
                  {logoUploading ? 'Uploading...' : 'Save Logo'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Company name */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Company Name *</label>
          <input
            type="text"
            value={company}
            onChange={e => setCompany(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {/* Bio / company description */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Company Description</label>
          <textarea
            value={bio}
            onChange={e => setBio(e.target.value)}
            rows={4}
            style={{ ...fieldStyle, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        {/* Location */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Location</label>
          <input
            type="text"
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="Houston, TX"
            style={fieldStyle}
          />
        </div>

        {/* Phone number */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Phone Number</label>
          <input
            type="text"
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {/* Company Email */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Company Email</label>
          <input
            type="text"
            value={companyEmail}
            onChange={e => setCompanyEmail(e.target.value)}
            placeholder="contact@yourcompany.com"
            style={fieldStyle}
          />
        </div>

        {/* Company Website */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Company Website</label>
          <input
            type="text"
            value={companyWebsite}
            onChange={e => setCompanyWebsite(e.target.value)}
            onBlur={handleWebsiteBlur}
            placeholder="https://yourcompany.com"
            style={fieldStyle}
          />
        </div>

        {/* Business Type */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Business Type</label>
          <select
            value={businessType}
            onChange={e => setBusinessType(e.target.value)}
            style={fieldStyle}
          >
            <option value="">— Select type —</option>
            {BUSINESS_TYPE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {businessType === 'Other' && (
            <input
              type="text"
              value={businessTypeOther}
              onChange={e => setBusinessTypeOther(e.target.value)}
              placeholder="Describe your business type"
              style={{ ...fieldStyle, marginTop: '0.5rem' }}
            />
          )}
        </div>

        {/* Industry */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Industry</label>
          <select
            value={industry}
            onChange={e => setIndustry(e.target.value)}
            style={fieldStyle}
          >
            <option value="">— Select industry —</option>
            {INDUSTRY_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {industry === 'Other' && (
            <input
              type="text"
              value={industryOther}
              onChange={e => setIndustryOther(e.target.value)}
              placeholder="Describe your industry"
              style={{ ...fieldStyle, marginTop: '0.5rem' }}
            />
          )}
        </div>

        {/* Save */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: '160px',
              backgroundColor: '#E8590C',
              color: 'white',
              padding: '0.65rem',
              borderRadius: '6px',
              border: 'none',
              fontSize: '15px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saveStatus === 'success' && (
            <span style={{ color: '#065f46', fontSize: '14px', fontWeight: '500' }}>{saveMessage}</span>
          )}
          {saveStatus === 'error' && (
            <span style={{ color: '#991b1b', fontSize: '14px' }}>{saveMessage}</span>
          )}
        </div>

        {/* PHASE 2 — Subscription summary */}
        {/* Show planTier badge, slot usage, upgrade CTA */}
        {/* Add here when plan tier feature ships */}
      </div>
    </div>
  );
}

export default CompanyProfilePage;
